import React, { useCallback } from 'react';
import {
  Form,
  required,
  useNotify,
  useRecordContext,
  useRefresh,
  useResourceContext,
  useUpdate,
} from 'react-admin';

import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Typography,
} from '@mui/material';

import AddCommentIcon from '@mui/icons-material/AddComment';
import useDialogStatus from '../../hooks/useDialogStatus';
import CommentInput from './CommentInput';
import DraggableDialog from '../layout/dialog/DraggableDialog';

const handleId = 'create-comment-dialog-title';

const CreateComment = ({
  floating,
  customLabel,
  caseId,
}: {
  floating?: boolean;
  customLabel?: string;
  caseId?: string;
}) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const resource = useResourceContext();
  const record = useRecordContext();
  const {
    open,
    openDialog,
    closeDialog,
  } = useDialogStatus();

  const [update] = useUpdate();

  const handleSubmit = useCallback((data: { comment?: string }) => {
    update(
      resource,
      {
        id: `${caseId ?? record?.id}/comment`,
        data: {
          comment: data.comment,
        },
      },
      {
        onSuccess: () => {
          refresh();
          closeDialog();
        },
        onError: () => {
          notify('Could not create comment', { type: 'error' });
        },
      },
    );
  }, [update, resource, caseId, record?.id, refresh, closeDialog, notify]);

  const label = customLabel ?? 'Add comment';

  return (
    <>
      {floating ? (
        <Fab
          onClick={openDialog}
          color="secondary"
          variant="extended"
          sx={{
            height: 'fit-content',
            paddingX: 2,
            paddingY: 1.5,
            borderRadius: '10px 10px 2px 10px',
          }}
        >
          <AddCommentIcon fontSize="small" />
          <Typography variant="button" marginLeft={2}>
            {label}
          </Typography>
        </Fab>
      ) : (
        <Button startIcon={<AddCommentIcon />} onClick={openDialog}>
          {label}
        </Button>
      )}

      <DraggableDialog open={open} onClose={closeDialog} fullWidth maxWidth="md" handleId={handleId}>
        <Form record={{ comment: '' }} onSubmit={handleSubmit}>
          <DialogTitle id={handleId}>Create comment</DialogTitle>
          <DialogContent>
            <CommentInput
              source="comment"
              validate={required()}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} color="secondary">
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Comment
            </Button>
          </DialogActions>
        </Form>
      </DraggableDialog>
    </>
  );
};

export default CreateComment;
